// Facebook Conversions API client for server-side event tracking

import type { FacebookParams } from './facebook-utils';

interface ConversionsAPIEvent {
  eventType: string;
  eventData: {
    email?: string;
    platform?: string;
    url?: string;
    fromLanguage?: string;
    toLanguage?: string;
    action?: string;
    element?: string;
    sourceUrl?: string;
    country?: string;
    country_code?: string;
    fbp?: string;        // Facebook browser ID for EMQ improvement
    fbc?: string;        // Facebook click ID for EMQ improvement
  };
}

// Send event to Facebook Conversions API via our edge function
export const sendConversionsAPIEvent = async (
  event: ConversionsAPIEvent
): Promise<{ success: boolean; eventId?: string }> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/facebook-conversions`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...event,
        eventData: {
          ...event.eventData,
          sourceUrl: window.location.href
        }
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Conversions API error:', errorData);
      return { success: false };
    }

    const result = await response.json();
    console.log('Conversions API success:', result);
    return { 
      success: true, 
      eventId: result.eventId
    };

  } catch (error) {
    console.error('Failed to send Conversions API event:', error);
    return { success: false };
  }
};

// Specific event tracking functions
export const trackNewsletterConversion = async (
  email: string, 
  countryData?: { country: string; country_code: string } | null,
  facebookParams?: FacebookParams
): Promise<{ success: boolean; eventId?: string }> => {
  return sendConversionsAPIEvent({
    eventType: 'newsletter_subscription',
    eventData: { 
      email,
      country: countryData?.country,
      country_code: countryData?.country_code,
      fbp: facebookParams?.fbp,
      fbc: facebookParams?.fbc
    }
  });
};

export const trackMusicPlatformConversion = async (
  platform: string, 
  url: string,
  facebookParams?: FacebookParams
): Promise<{ success: boolean; eventId?: string }> => {
  return sendConversionsAPIEvent({
    eventType: 'music_platform_click',
    eventData: { 
      platform, 
      url,
      fbp: facebookParams?.fbp,
      fbc: facebookParams?.fbc
    }
  });
};

export const trackLanguageChangeConversion = async (
  fromLanguage: string, 
  toLanguage: string
): Promise<{ success: boolean; eventId?: string }> => {
  return sendConversionsAPIEvent({
    eventType: 'language_change',
    eventData: { fromLanguage, toLanguage }
  });
};

export const trackEngagementConversion = async (
  action: string, 
  element: string
): Promise<{ success: boolean; eventId?: string }> => {
  return sendConversionsAPIEvent({
    eventType: 'user_engagement',
    eventData: { action, element }
  });
};